import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Container, Input, Modal, ModalBody, ModalHeader } from "reactstrap";
import { Truck, AlertTriangle, Phone } from "react-feather";
import { fetchBranches } from "../../utils/apiCalls";
import {
  getArrivals,
  getArrivalEvents,
  acknowledgeArrival,
  handOverArrival,
  disputeArrival,
} from "../../utils/fulfillmentApi";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";
import {
  MODE_LABELS,
  ARRIVAL_STATUS_LABELS,
  ARRIVAL_STAGE_LABELS,
  NOTIFY_OUTCOME_LABELS,
  ARRIVAL_EVENT_LABELS,
  CLOSED_ARRIVAL_STATUSES,
} from "../../constants/fulfillment";

// Silent refresh, matching waitlist-list: no spinner on the interval, because a queue that
// flashes every 15 seconds is unreadable while someone is trying to act on it.
const POLL_MS = 15000;

// Long enough that it means something has gone wrong, short enough to act on.
const OVERDUE_SECONDS = 5 * 60;

// Action buttons sit in a dense table row, so they stay compact and outline-only — a row of
// filled buttons reads as more urgent than the queue itself.
const actionBtn = (color, bg, busy) => ({
  background: bg,
  border: `1px solid ${color}`,
  borderRadius: 6,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 600,
  color,
  cursor: busy ? "not-allowed" : "pointer",
  opacity: busy ? 0.5 : 1,
  whiteSpace: "nowrap",
});

const fmtWait = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}:${String(s % 60).padStart(2, "0")}`;
};

const fmtDistance = (m) => {
  if (m === null || m === undefined) return "—";
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
};

/**
 * Head-office view of everyone currently waiting in a car.
 *
 * This exists on top of the KDS queue for one reason: when a branch tablet is offline the
 * customer's arrival still persists, and nobody in the branch can see it. Here it shows up
 * flagged, with the customer's and the branch's phone numbers, so someone can pick up a
 * phone. That case is called out as its own tile rather than buried in a column.
 */
const ArrivalsList = () => {
  const selectedMerchant = useSelector((state) => state.merchant?.selectedMerchant);
  const userType = localStorage.getItem("userType");

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [arrivals, setArrivals] = useState([]);
  const [summary, setSummary] = useState({ count: 0, waiting: 0, unreachable: 0 });
  const [includeClosed, setIncludeClosed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [, setTick] = useState(0);

  // Which arrival is mid-action. Keyed by id rather than a plain boolean so the operator can
  // see exactly which row is in flight.
  const [actingOn, setActingOn] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Browser notification only for arrivals we have not already announced, so a 15s poll
  // does not re-notify about the same car forever.
  const notifiedRef = useRef(new Set());

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") {
        list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      }
      // Branch-scoped operators only pick from their own branches — the server enforces
      // this too, this just keeps the picker honest.
      if (userType === "waitlist_staff") {
        const assigned = JSON.parse(localStorage.getItem("assignedBranchIds") || "[]").map(String);
        list = list.filter((b) => assigned.includes(String(b.id)));
      }
      setBranches(list);
    };
    load();
  }, [selectedMerchant, userType]);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    const params = {};
    // Platform admins have no MerchantId of their own, so without this the list would mix
    // every brand together. Merchant-scoped users are pinned server-side regardless.
    if (selectedMerchant && selectedMerchant !== "all") params.merchantId = selectedMerchant;
    if (selectedBranch) params.branchId = selectedBranch;
    if (includeClosed) params.includeClosed = "true";

    const data = await getArrivals(params);
    setArrivals(data?.arrivals || []);
    setSummary({
      count: data?.count || 0,
      waiting: data?.waiting || 0,
      unreachable: data?.unreachable || 0,
    });

    // Notify once per arrival the branch could not be told about — that is the one that
    // needs a human to intervene.
    (data?.arrivals || []).forEach((a) => {
      const unreachable = a.notifyOutcome && a.notifyOutcome !== "dispatched";
      if (unreachable && a.status === "waiting" && !notifiedRef.current.has(a.id)) {
        notifiedRef.current.add(a.id);
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("Customer waiting — branch not reachable", {
            body: `${a.customerName || "Customer"} at ${a.branchName || "a branch"} · order #${a.orderRef}`,
            icon: "/favicon.ico",
          });
        }
      }
    });

    if (!silent) setIsLoading(false);
  }, [selectedMerchant, selectedBranch, includeClosed]);

  // A branch filter from the previous merchant would silently return nothing.
  useEffect(() => { setSelectedBranch(""); }, [selectedMerchant]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const id = setInterval(() => {
      fetchData({ silent: true });
      // Re-render so the wait timers advance between polls.
      setTick((t) => t + 1);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  const openHistory = async (arrival) => {
    setHistoryFor(arrival);
    setHistoryLoading(true);
    setHistory(await getArrivalEvents(arrival.orderId));
    setHistoryLoading(false);
  };

  const runAction = async (arrival, action) => {
    if (actingOn) return;
    setActingOn(arrival.id);
    const ok = await action(arrival.orderId);
    // Refresh either way: a failure is usually "someone else already did it", and the fresh
    // list is exactly what the operator needs to see.
    await fetchData({ silent: true });
    setActingOn(null);
    return ok;
  };

  const statusBadge = (a) => {
    if (a.status === "waiting") {
      const overdue = a.waitingSeconds > OVERDUE_SECONDS;
      return <Badge color={overdue ? "#B91C1C" : C.orange} bg={overdue ? "#FEF2F2" : C.orangeLight}>
        {overdue ? "Waiting too long" : ARRIVAL_STATUS_LABELS.waiting}
      </Badge>;
    }
    if (a.status === "acknowledged") return <Badge color={C.coral} bg={C.coralLight}>{ARRIVAL_STATUS_LABELS.acknowledged}</Badge>;
    if (a.status === "handed_over") return <Badge color={C.green} bg={C.greenLight}>{ARRIVAL_STATUS_LABELS.handed_over}</Badge>;
    return <Badge color={C.textMed} bg={C.borderLight}>{ARRIVAL_STATUS_LABELS[a.status] || a.status}</Badge>;
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Curbside Arrivals" parent="Orders" />
      <Container fluid>
        <div className="rpt-wrap">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 18 }}>
            <Tile label="Waiting now" value={summary.waiting} />
            <Tile label="Open arrivals" value={summary.count} />
            {/* Its own tile because it is the only state that needs head office to act. */}
            <Tile label="Branch unreachable" value={summary.unreachable} />
          </div>

          <RCard>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
              <Input
                type="select"
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                style={{ maxWidth: 260, fontSize: 13 }}
              >
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Input>

              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.textMed, margin: 0 }}>
                <input
                  type="checkbox"
                  checked={includeClosed}
                  onChange={(e) => setIncludeClosed(e.target.checked)}
                  style={{ accentColor: C.coral }}
                />
                Include completed
              </label>
            </div>

            {isLoading ? (
              <Loader />
            ) : !arrivals.length ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: C.textLight, fontSize: 14 }}>
                <Truck size={28} color={C.border} />
                <div style={{ marginTop: 10 }}>Nobody is waiting in a car right now</div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>Waiting</th>
                      <th style={tbl.th}>Order</th>
                      <th style={tbl.th}>Branch</th>
                      <th style={tbl.th}>Vehicle</th>
                      <th style={tbl.th}>Mode</th>
                      <th style={tbl.th}>Location</th>
                      <th style={tbl.th}>Status</th>
                      <th style={tbl.th}>Notified</th>
                      <th style={tbl.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {arrivals.map((a) => {
                      const unreachable = a.notifyOutcome && a.notifyOutcome !== "dispatched";
                      const v = a.vehicle || {};
                      const car = [v.color, v.make, v.model].filter(Boolean).join(" ") || a.carInformation || "—";
                      return (
                        <tr key={a.id} style={unreachable ? { background: "#FEF2F2" } : undefined}>
                          <td style={{ ...tbl.td, fontWeight: 700, color: a.waitingSeconds > OVERDUE_SECONDS ? "#B91C1C" : C.text }}>
                            {fmtWait(a.waitingSeconds)}
                          </td>
                          <td style={tbl.td}>
                            <div style={{ fontWeight: 600 }}>#{a.orderRef}</div>
                            <div style={{ fontSize: 11, color: C.textLight }}>{a.customerName || "—"}</div>
                          </td>
                          <td style={tbl.td}>{a.branchName || "—"}</td>
                          <td style={tbl.td}>
                            {/* Staff read the physical plate, so it leads. */}
                            {v.plate ? <div style={{ fontWeight: 700, letterSpacing: 0.5 }}>{v.plate}</div> : null}
                            <div style={{ fontSize: 12, color: C.textMed }}>{car}</div>
                          </td>
                          <td style={tbl.td}>
                            {MODE_LABELS[a.mode] || a.mode}
                            {a.stage === "approaching" && (
                              <div style={{ fontSize: 11, color: C.orange }}>{ARRIVAL_STAGE_LABELS.approaching}</div>
                            )}
                          </td>
                          <td style={tbl.td}>
                            {/* Advisory only — never enforced. Shown so a human can judge. */}
                            {a.verificationSource === "geofence" ? (
                              <Badge color={C.green} bg={C.greenLight}>Verified</Badge>
                            ) : (
                              <span style={{ fontSize: 12, color: C.textMed }}>
                                Manual{a.distanceM != null ? ` · ${fmtDistance(a.distanceM)}` : ""}
                              </span>
                            )}
                          </td>
                          <td style={tbl.td}>{statusBadge(a)}</td>
                          <td style={tbl.td}>
                            {unreachable ? (
                              <div>
                                <Badge color="#B91C1C" bg="#FEF2F2">
                                  <AlertTriangle size={11} style={{ verticalAlign: -1 }} />{" "}
                                  {NOTIFY_OUTCOME_LABELS[a.notifyOutcome] || a.notifyOutcome}
                                </Badge>
                                {(a.customerPhone || a.branchPhone) && (
                                  <div style={{ fontSize: 11, color: C.textMed, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                                    <Phone size={11} /> {a.customerPhone || a.branchPhone}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ fontSize: 12, color: C.textMed }}>
                                {NOTIFY_OUTCOME_LABELS[a.notifyOutcome] || "—"}
                                {a.notifyCount > 1 ? ` ×${a.notifyCount}` : ""}
                              </span>
                            )}
                          </td>
                          <td style={tbl.td}>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                              {/* Same three transitions the KDS offers. Gated on the arrival
                                  still being open rather than on one exact status, so a
                                  "not found" car can still be served if it turns up. */}
                              {!CLOSED_ARRIVAL_STATUSES.includes(a.status) && (
                                <Fragment>
                                  {a.status !== "acknowledged" && (
                                    <button
                                      type="button"
                                      disabled={actingOn === a.id}
                                      onClick={() => runAction(a, acknowledgeArrival)}
                                      title="Tells the customer someone is coming out"
                                      style={actionBtn(C.coral, C.coralLight, actingOn === a.id)}
                                    >
                                      {actingOn === a.id ? "…" : "Acknowledge"}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    disabled={actingOn === a.id}
                                    onClick={() => runAction(a, handOverArrival)}
                                    style={actionBtn(C.coral, C.coralLight, actingOn === a.id)}
                                  >
                                    {actingOn === a.id ? "…" : "Handed over"}
                                  </button>
                                  {a.status !== "disputed" && (
                                    <button
                                      type="button"
                                      disabled={actingOn === a.id}
                                      onClick={() => runAction(a, disputeArrival)}
                                      title="Nobody is at the bay — keeps the car in the queue but flags it"
                                      style={actionBtn("#B91C1C", "#FEF2F2", actingOn === a.id)}
                                    >
                                      Not here
                                    </button>
                                  )}
                                </Fragment>
                              )}
                              <button
                                type="button"
                                onClick={() => openHistory(a)}
                                style={{
                                  background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
                                  padding: "4px 10px", fontSize: 12, color: C.textMed, cursor: "pointer",
                                }}
                              >
                                History
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>
        </div>
      </Container>

      {/* Append-only history. Includes the REFUSED taps — which is what settles a customer
          insisting they tapped and staff insisting nothing came through. */}
      <Modal isOpen={!!historyFor} toggle={() => setHistoryFor(null)} size="lg" centered>
        <ModalHeader toggle={() => setHistoryFor(null)}>
          Arrival history — order #{historyFor?.orderRef}
        </ModalHeader>
        <ModalBody>
          {historyLoading ? (
            <Loader />
          ) : !history.length ? (
            <div style={{ color: C.textLight, fontSize: 13 }}>No history recorded</div>
          ) : (
            <div>
              {history.map((e) => (
                <div key={e.id} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.borderLight}` }}>
                  <div style={{ fontSize: 12, color: C.textLight, minWidth: 128 }}>
                    {new Date(e.createdAt).toLocaleString()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                      {ARRIVAL_EVENT_LABELS[e.type] || e.type}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMed }}>
                      by {e.source}
                      {e.metadata?.distanceM != null ? ` · ${fmtDistance(e.metadata.distanceM)} away` : ""}
                      {e.metadata?.availableAt ? ` · next allowed ${new Date(e.metadata.availableAt).toLocaleTimeString()}` : ""}
                      {e.metadata?.outcome ? ` · ${e.metadata.outcome}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default ArrivalsList;
